<script lang="ts" setup>
import { ref, computed } from 'vue';
import apolloClient from '@/apollo/client';
import { ApolloCacheUtils } from '@/utils/cache-utils';
import { useMutation } from '@vue/apollo-composable';
import { BucketDeleteDocument } from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BFormInput,
  BModal,
  BSpinner,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';

const userStore = useUserStore();

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  bucketId: number;
  bucketFriendlyName: string;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});
const bucketNameConfirmation = ref<string>('');

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(BucketDeleteDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.bucketDelete) {
    console.debug('Bucket deleted:', res.data.bucketDelete);
    mutationShowSuccess.value = true;

    // Delete the bucket from apollo cache
    if (res.data.bucketDelete.id && userStore.id) {
      ApolloCacheUtils.removeUserBucket(
        apolloClient.cache,
        userStore.id,
        res.data.bucketDelete.id
      );
    }

    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Data deletion failed: ' + res.data;
    console.error('Bucket deletion failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error deleting bucket:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onDeletionConfirmed() {
  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({ bucketId: props.bucketId });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="trash" class="me-2" />
        Delete data
      </template>
      <!-- Content -->
      <div>
        <!-- Delete? Confirmation Message -->
        <h5>Delete {{ props.bucketFriendlyName }}?</h5>
        <b-badge variant="danger" class="mb-2">
          <font-awesome-icon icon="exclamation-triangle" />
          This action is irreversible
        </b-badge>
        <p>
          <strong> All associated data will be permanently deleted. </strong>
        </p>
        <p>
          If you wish to proceed with deletion, please type in the name of the
          data and click the button bellow.
        </p>
        <!-- Input for bucket name -->
        <p>
          <strong>
            Type
            <span class="confirmation-text">
              {{ props.bucketFriendlyName }}
            </span>
            for confirmation:
          </strong>
        </p>
        <b-form-input
          v-model="bucketNameConfirmation"
          type="text"
          placeholder="Type the name of the data to confirm deletion"
          :disabled="mutationInProgress || mutationShowSuccess"
          class="mb-3"
        ></b-form-input>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="
            mutationInProgress ||
            mutationShowSuccess ||
            bucketNameConfirmation !== props.bucketFriendlyName
          "
          @click="onDeletionConfirmed"
        >
          <span v-if="mutationInProgress">
            Deleting Data
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Data Deleted
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Delete Data </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<style scoped>
.confirmation-text {
  color: white;
  background-color: #6c757d;
  border-radius: 0.25rem;
  padding: 0.15rem 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
}
</style>
